import React from "react";

export interface StabilityEvent {
  id: string;
  atMs: number;
  hubName: string;
  kind: "degraded" | "recovered" | "stale";
}

const KIND_COLOR: Record<StabilityEvent["kind"], string> = {
  degraded: "var(--color-danger)",
  recovered: "var(--color-success)",
  stale: "var(--color-neutral)",
};

const DEFAULT_WINDOW_MS = 90 * 60 * 1000;

export interface StabilityTimelineProps {
  events: StabilityEvent[];
  windowMs?: number;
}

/**
 * Connection stability strip — ticks mark hub health *transitions* observed
 * live via `host:topology` SSE events while this page has been open.
 *
 * There is no backend endpoint that persists a health-change history (the
 * API contract only exposes each hub's *current* health), so this is
 * honestly scoped to the current browser session rather than fabricating a
 * historical timeline. See HostTopology's transition-tracking effect for
 * how `events` is built.
 */
export default function StabilityTimeline({ events, windowMs = DEFAULT_WINDOW_MS }: StabilityTimelineProps) {
  const now = Date.now();
  const windowStart = now - windowMs;
  const visible = events.filter((e) => e.atMs >= windowStart);
  const windowMinutes = Math.round(windowMs / 60_000);

  return (
    <div>
      <div
        style={{
          position: "relative",
          height: 44,
          background: "var(--color-foreground)",
          borderRadius: "var(--radius-sm)",
          margin: "8px 0 4px",
          overflow: "hidden",
        }}
      >
        {visible.map((e) => {
          const pct = Math.min(100, Math.max(0, ((e.atMs - windowStart) / windowMs) * 100));
          return (
            <span
              key={e.id}
              title={`${e.hubName} — ${e.kind} at ${new Date(e.atMs).toLocaleTimeString()}`}
              style={{
                position: "absolute",
                left: `${pct}%`,
                top: 8,
                height: 28,
                width: 3,
                borderRadius: 2,
                opacity: 0.85,
                background: KIND_COLOR[e.kind],
              }}
            />
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: "var(--color-text-secondary)",
        }}
      >
        <span>-{windowMinutes}m</span>
        <span>-{Math.round(windowMinutes * (2 / 3))}m</span>
        <span>-{Math.round(windowMinutes / 3)}m</span>
        <span>now</span>
      </div>
      <p
        style={{
          fontSize: "var(--font-size-xs)",
          color: "var(--color-text-secondary)",
          marginTop: 6,
        }}
      >
        {visible.length === 0
          ? "No hub health changes observed since this page loaded — ticks appear live as host:topology events arrive."
          : `${visible.length} health change(s) observed this session (live only — not a persisted history).`}
      </p>
    </div>
  );
}
